import { z } from 'zod';
import { AssetTypeSchema, DeviceSlotSchema, ExperimentStateSchema, OutcomeVerdictSchema, TargetSchema } from './domain.js';
export const EXPERIMENTS_PATH = '/api/v1/experiments';
export const CANDIDATES_PATH = '/api/v1/candidates';
export const CANDIDATE_SLOTS_PATH = '/api/v1/candidate-slots';
export const SCREENSHOT_ITEMS_PATH = '/api/v1/screenshot-items';
export const EXPERIMENT_RESOURCE = 'experiment';
export const CANDIDATE_BATCH_RESOURCE = 'candidateBatch';
export const CANDIDATE_SLOT_RESOURCE = 'candidateSlot';
export const CANDIDATE_RESOURCE = 'candidate';
export const GENERATION_ATTEMPT_RESOURCE = 'generationAttempt';
/** How many complete Candidates the first Generate and every `Explore 6 more` produce. */
export const SPREAD_SIZE = 6;
/** How many descendants `More like this` produces. */
export const BRANCH_SIZE = 4;
/**
 * A slot is `working` from the moment it exists, which is before any provider has been
 * called. `ready` is the only state with a Candidate behind it: there is no state for a
 * half-finished Candidate because no such row is ever written.
 */
export const CandidateSlotStateSchema = z.enum(['working', 'ready', 'failed', 'needsAttention', 'cancelled']);
export const CandidateBatchKindSchema = z.enum(['initial', 'explore', 'branch']);
/** A branch either reworks the whole set or one slide of it. Both make a descendant. */
export const CandidateBatchScopeSchema = z.enum(['wholeSet', 'slide']);
export const StoryRoleSchema = z.enum(['hook', 'coreValue', 'proof', 'feature', 'differentiator', 'closing']);
export const SlideSourceKindSchema = z.enum(['productScreenshot', 'generatedScene', 'referenceDerived', 'mixed']);
/** The four axes a spread varies across, as the gallery shows them. */
export const CreativeDirectionSchema = z.object({
    marketingAngle: z.string(),
    visualMetaphor: z.string(),
    style: z.string(),
    composition: z.string(),
});
export const AppliedLearningSchema = z.object({ learningId: z.string().min(1), text: z.string() });
/**
 * How a closed Experiment turned out, small enough to sit on a history card.
 *
 * The whole Outcome - the note, every piece of evidence and every number read out of it
 * - is read from the Learn stage. What a card needs is the verdict, the one number that
 * answers `by how much`, and when it was settled. Without those three on the list
 * resource a history card either says nothing or opens twelve Experiments to find out,
 * and both of those were true of this build.
 */
export const ExperimentOutcomeSummarySchema = z.object({
    verdict: OutcomeVerdictSchema,
    /** The uplift as it was extracted or typed. Absent is an ordinary result. */
    uplift: z.object({ label: z.string(), value: z.string(), unit: z.string().nullable() }).nullable(),
    resultDate: z.string().min(1),
});
export const ExperimentResourceSchema = z.object({
    id: z.string().min(1),
    version: z.number().int().nonnegative(),
    appId: z.string().min(1),
    /** The Store Listing this Experiment is for; null is the App's Main Store Listing. */
    storeListingId: z.string().nullable(),
    target: TargetSchema,
    assetType: AssetTypeSchema,
    /** Explicit for a screenshot Experiment, absent for every other Asset Type. */
    deviceSlot: DeviceSlotSchema.nullable(),
    brief: z.string(),
    hypothesis: z.string().nullable(),
    /** True while the Hypothesis is still the Studio's wording rather than the operator's. */
    hypothesisProposed: z.boolean(),
    state: ExperimentStateSchema,
    materialIds: z.array(z.string().min(1)),
    appliedLearnings: z.array(AppliedLearningSchema),
    /** Source Candidates carried in by `Repeat for...`. References, never parentage. */
    sourceCandidateIds: z.array(z.string().min(1)),
    /** Present once this Experiment was closed with an Outcome, and null before that. */
    outcome: ExperimentOutcomeSummarySchema.nullable(),
    /** False once any Batch, Slot, Attempt or started Job exists. */
    attributionEditable: z.boolean(),
    createdAt: z.string().min(1),
    updatedAt: z.string().min(1),
});
export const CreateExperimentRequestSchema = z.object({
    appId: z.string().min(1),
    assetType: AssetTypeSchema,
    storeListingId: z.string().min(1).nullable().optional(),
    target: TargetSchema.optional(),
    deviceSlot: DeviceSlotSchema.optional(),
    brief: z.string().optional(),
    hypothesis: z.string().nullable().optional(),
    materialIds: z.array(z.string().min(1)).optional(),
    learnings: z.array(AppliedLearningSchema).optional(),
    sourceCandidateIds: z.array(z.string().min(1)).optional(),
});
/**
 * Everything a Brief holds. `hypothesis: null` removes the proposal rather than
 * leaving it in place, which is the difference between editable and authoritative.
 *
 * The lifecycle state is deliberately absent, and the schema is strict so that sending
 * one is refused instead of quietly ignored. `Mark Live` and closing are the only two
 * ways an Experiment changes state, because both of them do something besides moving a
 * word: one freezes the Control, the other writes an Outcome. A `PATCH` that set the
 * state would reach `live` with nothing frozen and `closed` with no Outcome at all.
 */
export const UpdateExperimentRequestSchema = z
    .object({
    storeListingId: z.string().min(1).nullable().optional(),
    target: TargetSchema.optional(),
    assetType: AssetTypeSchema.optional(),
    deviceSlot: DeviceSlotSchema.optional(),
    brief: z.string().optional(),
    hypothesis: z.string().nullable().optional(),
    materialIds: z.array(z.string().min(1)).optional(),
})
    .strict();
export const ScreenshotItemResourceSchema = z.object({
    id: z.string().min(1),
    position: z.number().int().nonnegative(),
    storyRole: StoryRoleSchema,
    copy: z.string(),
    sourceKind: SlideSourceKindSchema,
    framing: z.string(),
    sourceLibraryItemId: z.string().nullable(),
    artifactId: z.string().min(1),
});
export const StoryBeatSchema = z.object({
    index: z.number().int().nonnegative(),
    role: StoryRoleSchema,
    copy: z.string(),
    sourceKind: SlideSourceKindSchema,
    framing: z.string(),
});
/** Editable context, not a form and not an approval step. */
export const StoryPlanResourceSchema = z.object({
    beats: z.array(StoryBeatSchema),
    edited: z.boolean(),
    version: z.number().int().nonnegative(),
});
export const CandidateResourceSchema = z.object({
    id: z.string().min(1),
    version: z.number().int().nonnegative(),
    experimentId: z.string().min(1),
    parentId: z.string().nullable(),
    assetType: AssetTypeSchema,
    direction: CreativeDirectionSchema,
    headline: z.string(),
    summary: z.string(),
    /** What a branch changed about its parent, in the operator's own words. */
    refinement: z.string().nullable(),
    artifactId: z.string().nullable(),
    keep: z.boolean(),
    hidden: z.boolean(),
    /** The complete ordered set. Empty for the single-asset types. */
    items: z.array(ScreenshotItemResourceSchema),
    storyPlan: StoryPlanResourceSchema.nullable(),
    createdAt: z.string().min(1),
    updatedAt: z.string().min(1),
});
export const CandidateSlotResourceSchema = z.object({
    id: z.string().min(1),
    version: z.number().int().nonnegative(),
    batchId: z.string().min(1),
    slotIndex: z.number().int().nonnegative(),
    state: CandidateSlotStateSchema,
    /** One plain line. Provider detail stays under `Details` on the Attempt. */
    message: z.string().nullable(),
    jobId: z.string().nullable(),
    attemptId: z.string().nullable(),
    candidateId: z.string().nullable(),
    /** Readable while the slot is still working, so six pending slots are not six blanks. */
    headline: z.string(),
    direction: CreativeDirectionSchema,
});
export const CandidateBatchResourceSchema = z.object({
    id: z.string().min(1),
    experimentId: z.string().min(1),
    kind: CandidateBatchKindSchema,
    scope: CandidateBatchScopeSchema,
    scopeItemPosition: z.number().int().nonnegative().nullable(),
    spreadIndex: z.number().int().nonnegative(),
    size: z.number().int().positive(),
    parentCandidateId: z.string().nullable(),
    instruction: z.string().nullable(),
    createdAt: z.string().min(1),
    slots: z.array(CandidateSlotResourceSchema),
});
export const ExperimentDetailSchema = z.object({
    experiment: ExperimentResourceSchema,
    batches: z.array(CandidateBatchResourceSchema),
    candidates: z.array(CandidateResourceSchema),
});
/** `Generate` and `Explore 6 more` are the same command with a different batch kind. */
export const SpreadRequestSchema = z.object({ kind: z.enum(['initial', 'explore']).optional() });
/** One natural-language instruction and optional new references. No prompt engineering. */
export const BranchRequestSchema = z.object({
    instruction: z.string().optional(),
    materialIds: z.array(z.string().min(1)).optional(),
    count: z.number().int().min(1).max(4).optional(),
});
/**
 * Editing one slide. The other slides are inherited unchanged into the descendant.
 *
 * A slide's source is replaceable, not only visible: naming App Library material puts
 * those bytes in the slide directly, with no provider call and no weights or locks to
 * manage. An instruction asks for the slide to be made again instead; giving both sends
 * the named material into that generation as a reference.
 */
export const EditSlideRequestSchema = z.object({
    instruction: z.string().optional(),
    materialIds: z.array(z.string().min(1)).optional(),
});
/**
 * What a slide edit came back as. The discriminant lives in the body on purpose: the
 * operator performed one action and only its price differed, so a caller branches on
 * `kind` rather than reading meaning into 201 against 202.
 */
export const SlideEditResponseSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('replaced'), candidate: CandidateResourceSchema }),
    z.object({ kind: z.literal('regenerating'), batch: CandidateBatchResourceSchema }),
]);
export const ReorderItemsRequestSchema = z.object({ itemIds: z.array(z.string().min(1)).min(1) });
/** Keep and Hide are lightweight presentation flags, so they travel together. */
export const CandidateFlagsRequestSchema = z.object({ keep: z.boolean().optional(), hidden: z.boolean().optional() });
export const StoryPlanRequestSchema = z.object({ beats: z.array(StoryBeatSchema).min(1) });
export const UseLearningRequestSchema = AppliedLearningSchema;
/** The technical record behind a slot, kept under `Details` and out of the gallery. */
export const GenerationAttemptResourceSchema = z.object({
    id: z.string().min(1),
    version: z.number().int().nonnegative(),
    slotId: z.string().nullable(),
    providerId: z.string(),
    modelId: z.string(),
    operation: z.string(),
    reason: z.enum(['initial', 'retry', 'fallback']),
    attemptNumber: z.number().int().positive(),
    status: z.enum(['open', 'submitted', 'succeeded', 'failed', 'needsAttention']),
    /**
     * `subscription` is an Attempt run on a signed-in subscription account: the Studio
     * cannot see what it consumed, so both amounts are null and stay null.
     */
    accounting: z.enum(['usd', 'subscription']),
    estimatedUsd: z.number().nullable(),
    /** Present once the provider reported it; otherwise the estimate stays visible. */
    actualUsd: z.number().nullable(),
    failureReason: z.string().nullable(),
    message: z.string().nullable(),
    /** The Artifacts this Attempt was pinned to, as references, with their roles kept in `plan`. */
    referenceArtifactIds: z.array(z.string().min(1)),
    producedArtifactIds: z.array(z.string().min(1)),
    createdAt: z.string().min(1),
});
//# sourceMappingURL=experiment.js.map